import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { useAccount, useDisconnect } from 'wagmi'
import { Networks } from '@/utils/currencies'
import { Button } from '@/components/Button/Button'
import {
  useWallets,
  useConnect,
  type UiWallet,
  type UiWalletAccount,
} from '@wallet-standard/react'
import { SelectedWalletAccountContext } from '@/contexts/WalletAccountProvider'
import * as Sentry from '@sentry/react'
import * as Dialog from '@radix-ui/react-dialog'
import * as Icons from '@radix-ui/react-icons'
import { Wallet } from 'lucide-react'
import { track } from '@/utils/analytics'
import { isMobileBrowser, isPhantomBrowser } from '@/utils/phantomMobile'
import { PhantomSolanaMobileContext } from '@/contexts/PhantomSolanaMobileContext'
import { PhantomSolanaMobileConnectButton } from './PhantomSolanaMobileConnectButton'

type SimpleWalletConnectProps = {
  network: string
  onWalletConnect: (address: string) => void
  onError?: (error: Error) => void
  isValidManualAddress: boolean
  currentWallet: string
}
export const SimpleWalletConnect: React.FC<SimpleWalletConnectProps> = ({
  network,
  onWalletConnect,
  onError,
  isValidManualAddress,
  currentWallet,
}) => {
  const { disconnect } = useDisconnect()
  const { address: evmAddress, connector } = useAccount()
  const isEVM = network !== Networks.sol
  const [walletDialogOpen, setWalletDialogOpen] = useState(false)
  const [isManualConnection, setIsManualConnection] = useState(false)
  const lastTrackedAddressRef = useRef<string | null>(null)
  const {
    callbackError: phantomCallbackError,
    disconnect: disconnectPhantom,
    session: phantomSession,
    sessionCallbackSequence,
  } = useContext(PhantomSolanaMobileContext)
  const handledPhantomCallbackSequenceRef = useRef(sessionCallbackSequence)

  // Get the selected wallet account from context
  const [selectedWalletAccount, setSelectedWalletAccount] = useContext(
    SelectedWalletAccountContext
  )

  // Get available wallets
  const wallets = useWallets()

  // Filter for Solana wallets
  const availableSolanaWallets = wallets.filter((wallet) =>
    wallet.chains.includes('solana:mainnet')
  )

  // Function to disconnect current Solana wallet
  const disconnectSolanaWallet = useCallback(() => {
    if (phantomSession) {
      lastTrackedAddressRef.current = null
      disconnectPhantom()
      onWalletConnect('')
      return
    }
    if (selectedWalletAccount) {
      console.log(
        'Disconnecting current wallet account:',
        selectedWalletAccount.address
      )
      lastTrackedAddressRef.current = null
      setSelectedWalletAccount(undefined)
      onWalletConnect('')
    }
  }, [
    disconnectPhantom,
    onWalletConnect,
    phantomSession,
    selectedWalletAccount,
    setSelectedWalletAccount,
  ])

  useEffect(() => {
    if (isEVM || !phantomSession) return

    if (lastTrackedAddressRef.current !== phantomSession.walletPublicKey) {
      lastTrackedAddressRef.current = phantomSession.walletPublicKey
      track('wallet_connected', {
        network,
        provider: 'phantom',
      })
    }
    const lockedWalletAddress = sessionStorage.getItem('walletAddress')
    const isManualPhantomConnection =
      handledPhantomCallbackSequenceRef.current !== sessionCallbackSequence
    if (
      isManualPhantomConnection ||
      !lockedWalletAddress ||
      lockedWalletAddress.trim() === ''
    ) {
      onWalletConnect(phantomSession.walletPublicKey)
    }
    handledPhantomCallbackSequenceRef.current = sessionCallbackSequence
    setWalletDialogOpen(false)
    setIsManualConnection(false)
  }, [isEVM, network, onWalletConnect, phantomSession, sessionCallbackSequence])

  useEffect(() => {
    if (!phantomCallbackError) return

    setWalletDialogOpen(false)
    setIsManualConnection(false)
    onError?.(phantomCallbackError)
  }, [onError, phantomCallbackError])

  useEffect(() => {
    if (!isEVM) return

    if (!evmAddress) {
      lastTrackedAddressRef.current = null
      return
    }

    if (lastTrackedAddressRef.current === evmAddress) return
    lastTrackedAddressRef.current = evmAddress

    track('wallet_connected', {
      network,
      provider: 'evm',
      connector: connector?.id,
      connector_name: connector?.name,
    })

    if (isManualConnection) {
      onWalletConnect(evmAddress)
      setIsManualConnection(false)
      return
    }

    const urlWalletAddress = sessionStorage.getItem('walletAddress')
    if (!urlWalletAddress || urlWalletAddress.trim() === '') {
      onWalletConnect(evmAddress)
    }
  }, [
    connector,
    evmAddress,
    isEVM,
    isManualConnection,
    network,
    onWalletConnect,
  ])

  const handleSolanaConnect = async () => {
    try {
      track('wallet_connect_clicked', { network })
      // Mark this as a manual connection
      setIsManualConnection(true)

      // First disconnect the current wallet if connected
      if (selectedWalletAccount) {
        disconnectSolanaWallet()
        // Brief timeout to ensure disconnection is complete
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      // Open the wallet selection dialog
      setWalletDialogOpen(true)
    } catch (error) {
      // Capture Solana wallet errors in Sentry
      Sentry.captureException(error, {
        tags: {
          action: 'solana_wallet_connect',
          is_connected: selectedWalletAccount ? 'true' : 'false',
          wallet_address: selectedWalletAccount?.address,
          expected_address: currentWallet,
        },
      })
      console.error('Error handling Solana wallet connection:', error)
    }
  }

  // Handle accounts after successful connection
  const handleAccountsReceived = useCallback(
    (account: UiWalletAccount, walletName: string) => {
      console.log('Selected account:', account)
      if (account) {
        disconnectPhantom()
        track('wallet_connected', {
          network,
          provider: 'solana',
          connector_name: walletName,
        })
        // Set in the context (this will also save to localStorage)
        setSelectedWalletAccount(account)
        // Always update wallet for manual connections, only check URL parameter for automatic connections
        if (isManualConnection) {
          onWalletConnect(account.address)
        } else {
          const urlWalletAddress = sessionStorage.getItem('walletAddress')
          if (!urlWalletAddress || urlWalletAddress.trim() === '') {
            onWalletConnect(account.address)
          }
        }
      }
      setWalletDialogOpen(false)
      setIsManualConnection(false) // Reset after connection
    },
    [
      disconnectPhantom,
      isManualConnection,
      network,
      onWalletConnect,
      setSelectedWalletAccount,
    ]
  )

  // Use the ConnectButton component for each wallet
  const WalletConnectButton = ({
    wallet,
    onConnect,
  }: {
    wallet: UiWallet
    onConnect: (accounts: UiWalletAccount, walletName: string) => void
  }) => {
    const [isConnecting, connect] = useConnect(wallet)
    const handleConnectClick = useCallback(async () => {
      try {
        const nextAccounts = await connect()
        // Handle account selection if needed
        if (nextAccounts.length > 0) {
          onConnect(nextAccounts[0], wallet.name)
        }
      } catch (error: any) {
        console.error('Wallet connection error:', error)
        Sentry.captureException(error, {
          tags: {
            action: 'wallet_connect',
            wallet_name: wallet.name,
          },
        })
      }
    }, [connect, onConnect, wallet.name])
    return (
      <button
        onClick={handleConnectClick}
        disabled={isConnecting}
        className="w-full justify-start p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
        data-testid={`wallet-connect-${wallet.name.toLowerCase().replace(/\s+/g, '-')}-button`}
      >
        <div className="flex items-center gap-2">
          {wallet.icon && (
            <img
              src={wallet.icon}
              alt={`${wallet.name} icon`}
              className="w-6 h-6"
              data-testid={`wallet-connect-${wallet.name.toLowerCase().replace(/\s+/g, '-')}-icon`}
            />
          )}
          <span className="font-medium">{wallet.name}</span>
          {isConnecting && <span className="ml-auto">Connecting...</span>}
        </div>
      </button>
    )
  }
  const truncateAddress = (address: string): string => {
    if (!address) return ''
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  const connectedSolanaAddress =
    phantomSession?.walletPublicKey ?? selectedWalletAccount?.address

  // Solana wallet selection dialog
  const showPhantomMobile = isMobileBrowser() && !isPhantomBrowser()
  const WalletSelectDialog = () => (
    <Dialog.Root open={walletDialogOpen} onOpenChange={setWalletDialogOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-900 p-6 rounded-lg shadow-xl w-[90vw] max-w-md">
          <Dialog.Title className="text-xl font-semibold mb-4">
            {connectedSolanaAddress ? 'Switch Wallet' : 'Select a Wallet'}
          </Dialog.Title>
          {connectedSolanaAddress && (
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/30 rounded border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                You'll be disconnected from your current wallet (
                {truncateAddress(connectedSolanaAddress)}) when you connect to a
                new one.
              </p>
            </div>
          )}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {showPhantomMobile && (
              <PhantomSolanaMobileConnectButton
                onConnectStarted={() => {
                  lastTrackedAddressRef.current = null
                  setSelectedWalletAccount(undefined)
                  setIsManualConnection(true)
                }}
              />
            )}
            {availableSolanaWallets.length > 0 ? (
              availableSolanaWallets.map((wallet) => (
                <WalletConnectButton
                  key={wallet.name}
                  wallet={wallet}
                  onConnect={handleAccountsReceived}
                />
              ))
            ) : !showPhantomMobile ? (
              <div className="p-3 text-center text-gray-500">
                No Solana wallets found. Please install a Solana wallet
                extension.
              </div>
            ) : null}
          </div>
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label="Close"
              data-testid="btn-addwalletconnect-submit"
            >
              <Icons.Cross2Icon />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )

  // Check if connected to the expected Solana wallet
  const isSolConnectedWallet = connectedSolanaAddress === currentWallet
  const shouldDisableSolButton = isValidManualAddress && !isSolConnectedWallet
  if (isEVM) {
    return (
      <ConnectButton.Custom>
        {({ account, openConnectModal }) => {
          const ready = Boolean(account)
          const isConnectedWallet =
            account?.address?.toLowerCase() === currentWallet?.toLowerCase()
          const handleClick = () => {
            try {
              track('wallet_connect_clicked', { network })
              // Mark this as a manual connection
              setIsManualConnection(true)

              if (ready) {
                disconnect()
                onWalletConnect('')
                setTimeout(openConnectModal, 100)
              } else {
                openConnectModal()
              }
            } catch (error) {
              Sentry.captureException(error, {
                tags: {
                  action: 'wallet_button_click',
                  is_connected: ready ? 'true' : 'false',
                  wallet_address: account?.address,
                  expected_address: currentWallet,
                },
              })
              console.error('Button click error:', error)
            }
          }
          const shouldDisable = isValidManualAddress && !isConnectedWallet
          return (
            <Button
              onClick={handleClick}
              color="outline"
              disabled={shouldDisable}
              className={`h-12 px-4 text-sm font-medium ${shouldDisable ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="btn-addwalletconnect-submit"
            >
              <span className="flex items-center justify-center gap-2">
                <Wallet className="h-4 w-4" aria-hidden="true" />
                {ready ? 'Switch Wallet' : 'Connect'}
              </span>
            </Button>
          )
        }}
      </ConnectButton.Custom>
    )
  }
  return (
    <>
      <Button
        onClick={handleSolanaConnect}
        color="outline"
        disabled={shouldDisableSolButton}
        className={`h-12 px-4 text-sm font-medium ${shouldDisableSolButton ? 'opacity-50 cursor-not-allowed' : ''}`}
        data-testid="btn-addwalletconnect-submit"
      >
        <span className="flex items-center justify-center gap-2">
          <Wallet className="h-4 w-4" aria-hidden="true" />
          {connectedSolanaAddress ? 'Switch Wallet' : 'Connect'}
        </span>
      </Button>
      <WalletSelectDialog />
    </>
  )
}

/*
IMPROVEMENT SUGGESTIONS:
1. Error handling could be improved with more specific error messages for different failure scenarios
2. The component has mixed responsibilities - handling both EVM and Solana wallet connections. Consider splitting into separate components
3. Implement proper loading states during wallet connection operations
4. Add accessibility enhancements for the wallet dialog (keyboard navigation, focus management)
5. Consider implementing a timeout with retry mechanism for wallet connection attempts
6. Dynamic accounts selection UI could be improved when multiple accounts are available
7. Extract wallet connection logic into a custom hook to separate UI from connection logic
8. Add comprehensive logging for easier debugging of wallet connection issues
9. Consider adding an explicit cancel button in the dialog for better UX
10. Implement progressive enhancement for browsers without wallet support
*/
